import { Component } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Location } from '@angular/common';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import {
  faCircleArrowLeft,
  faPaperPlane,
  faPenToSquare,
  faTrash,
  faCirclePlus,
  faCircleArrowRight,
  faSearch,
  faHouse,
  faBuilding,
  faHouseChimney,
} from '@fortawesome/free-solid-svg-icons';
import { ToastrService } from 'ngx-toastr';
import { MessageResponse } from '../_models/responses';
import { environment } from '../../environments/environment';
import { Breach, BreachService } from '../_services';
import { PropertyService } from '../_services/property.service';
import { Property } from '../_models/property';

@Component({
  selector: 'app-manage-breach',
  standalone: false,

  templateUrl: './manage-breach.component.html',
  styleUrl: './manage-breach.component.css'
})
export class ManageBreachComponent {
  action: string = 'list';
  searchProperty = '';

  faCircleArrowLeft = faCircleArrowLeft;
  faPaperPlane = faPaperPlane;
  faPenToSquare = faPenToSquare;
  faTrash = faTrash;
  faCirclePlus = faCirclePlus;
  faCircleArrowRight = faCircleArrowRight;
  faSearch = faSearch;
  faHouse = faHouse;
  faBuilding = faBuilding;
  faHouseChimney = faHouseChimney;

  loading = false;
  loadingProperties = false;
  fetching = false;
  submitBreachData = false;

  searchBreach = '';

  breaches: Breach[] = [];
  properties: Property[] = [];
  property: any;
  selectedBreach: Breach | null = null;

  addBreachForm!: FormGroup;
  editBreachForm!: FormGroup;
  viewBreachForm!: FormGroup;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private _location: Location,
    private formBuilder: FormBuilder,
    private breachService: BreachService,
    private propertyService: PropertyService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe((params: Params) => {
      this.action = params['action'] || 'list';
      this.initializeForms();
      this.loadPropertyContextForAction();
    });
  }

  get fba() {
    return this.addBreachForm.controls;
  }

  get fbe() {
    return this.editBreachForm.controls;
  }

  goBack(): void {
    this._location.back();
  }

  onCreateBreach(): void {
    this.router.navigate(['/manage-breaches/create']);
  }

  onCancel(): void {
    this.router.navigate(['/manage-breaches/list']);
  }

  onSelectedBreach(breach: Breach): void {
    this.selectedBreach = breach;
    localStorage.setItem('breach', JSON.stringify(breach));

    this.viewBreachForm.patchValue({
      id: breach.id,
      title: breach.title,
      description: breach.description,
      firstname: breach.firstname,
      lastname: breach.lastname,
      middlename: breach.middlename || '',
      datereported: breach.datereported,
      createdBy: breach.createdBy,
      unit_number: breach.unit_number,
      propertyId: breach.propertyId,
      bylawid: breach.bylawid || '',
      status: breach.status,
      propertyName: this.property?.name || '',
    });

    this.router.navigate(['/manage-breaches/view']);
  }

  onEditBreach(): void {
    if (!this.selectedBreach) {
      this.toastr.error('No breach selected for editing.', 'Error');
      return;
    }

    this.editBreachForm.patchValue({
      id: this.selectedBreach.id,
      title: this.selectedBreach.title,
      description: this.selectedBreach.description,
      firstname: this.selectedBreach.firstname,
      lastname: this.selectedBreach.lastname,
      middlename: this.selectedBreach.middlename || '',
      datereported: this.selectedBreach.datereported,
      createdBy: this.selectedBreach.createdBy,
      unit_number: this.selectedBreach.unit_number,
      propertyId: this.selectedBreach.propertyId,
      bylawid: this.selectedBreach.bylawid || '',
      status: this.selectedBreach.status,
      propertyName: this.property?.name || '',
    });

    this.router.navigate(['/manage-breaches/edit']);
  }

  createBreach(): void {
    this.submitBreachData = true;
    this.fetching = true;

    if (!this.addBreachForm.valid) {
      this.fetching = false;
      return;
    }

    const formdata = this.addBreachForm.value;
    const payload = this.buildPayload(formdata);

    this.breachService.createBreach(payload).subscribe({
      next: (data: MessageResponse) => {
        this.toastr.success(data.message, 'Success');
        this.fetching = false;
        this.submitBreachData = false;
        this.loadBreachesByProperty();
        this.router.navigate(['/manage-breaches/list']);
      },
      error: (error: HttpErrorResponse) => {
        this.fetching = false;
        this.toastr.error('Failed to create breach.', 'Error');
        if (!environment.production) {
          console.log('Breach creation failed: ', error);
        }
      },
    });
  }

  updateBreach(): void {
    this.submitBreachData = true;
    this.fetching = true;

    if (!this.editBreachForm.valid) {
      this.fetching = false;
      return;
    }

    const formdata = this.editBreachForm.value;
    const breachId = formdata.id;
    const payload = this.buildPayload(formdata);

    this.breachService.updateBreach(breachId, payload).subscribe({
      next: (data: MessageResponse) => {
        this.toastr.success(data.message, 'Success');
        this.fetching = false;
        this.submitBreachData = false;
        localStorage.setItem('breach', JSON.stringify({ id: breachId, ...payload }));
        this.loadBreachesByProperty();
        this.router.navigate(['/manage-breaches/list']);
      },
      error: (error: HttpErrorResponse) => {
        this.fetching = false;
        this.toastr.error('Failed to update breach.', 'Error');
        if (!environment.production) {
          console.log('Breach update failed: ', error);
        }
      },
    });
  }

  onDeleteBreach(): void {
    const breachId = this.selectedBreach?.id || this.editBreachForm?.value?.id || this.viewBreachForm?.value?.id;
    if (!breachId) {
      this.toastr.error('No breach selected for deletion.', 'Error');
      return;
    }

    this.fetching = true;
    this.breachService.deleteBreach(breachId).subscribe({
      next: (data: MessageResponse) => {
        this.toastr.success(data.message, 'Success');
        this.fetching = false;
        localStorage.removeItem('breach');
        this.selectedBreach = null;
        this.loadBreachesByProperty();
        this.router.navigate(['/manage-breaches/list']);
      },
      error: (error: HttpErrorResponse) => {
        this.fetching = false;
        this.toastr.error('Failed to delete breach.', 'Error');
        if (!environment.production) {
          console.log('Breach delete failed: ', error);
        }
      },
    });
  }

  private initializeForms(): void {
    this.addBreachForm = this.formBuilder.group({
      title: ['', Validators.required],
      description: ['', Validators.required],
      firstname: ['', Validators.required],
      lastname: ['', Validators.required],
      middlename: [''],
      datereported: ['', Validators.required],
      createdBy: ['', Validators.required],
      unit_number: ['', Validators.required],
      propertyId: ['', Validators.required],
      bylawid: [''],
      status: ['open', Validators.required],
      propertyName: [''],
    });

    this.editBreachForm = this.formBuilder.group({
      id: ['', Validators.required],
      title: ['', Validators.required],
      description: ['', Validators.required],
      firstname: ['', Validators.required],
      lastname: ['', Validators.required],
      middlename: [''],
      datereported: ['', Validators.required],
      createdBy: ['', Validators.required],
      unit_number: ['', Validators.required],
      propertyId: ['', Validators.required],
      bylawid: [''],
      status: ['open', Validators.required],
      propertyName: [''],
    });

    this.viewBreachForm = this.formBuilder.group({
      id: [{ value: '', disabled: true }, Validators.required],
      title: [{ value: '', disabled: true }, Validators.required],
      description: [{ value: '', disabled: true }, Validators.required],
      firstname: [{ value: '', disabled: true }, Validators.required],
      lastname: [{ value: '', disabled: true }, Validators.required],
      middlename: [{ value: '', disabled: true }],
      datereported: [{ value: '', disabled: true }, Validators.required],
      createdBy: [{ value: '', disabled: true }, Validators.required],
      unit_number: [{ value: '', disabled: true }, Validators.required],
      propertyId: [{ value: '', disabled: true }, Validators.required],
      bylawid: [{ value: '', disabled: true }],
      status: [{ value: '', disabled: true }, Validators.required],
      propertyName: [{ value: '', disabled: true }],
    });
  }

  private loadPropertyContextForAction(): void {
    this.loadingProperties = true;
    this.propertyService.getMyPropertyAssociations().subscribe({
      next: (associations: any[]) => {
        this.properties = associations.map(assoc => ({
          id: assoc.propertyId,
          name: assoc.propertyDetails.name,
          property_type: assoc.propertyDetails.property_type,
          address: assoc.propertyDetails.address,
          obligationCategoryId: assoc.propertyDetails.obligationCategoryId ?? 0,
          premises: []
        })) || [];

        if (this.properties.length === 1) {
          this.setSelectedProperty(this.properties[0]);
        } else {
          const propertystr = localStorage.getItem('property');
          const currentProperty = propertystr ? JSON.parse(propertystr) : null;
          if (currentProperty?.id) {
            const matched = this.properties.find((p) => p.id === currentProperty.id);
            if (matched) {
              this.setSelectedProperty(matched);
            }
          }
        }

        if (this.properties.length > 1 && this.action === 'list') {
          this.action = 'index';
        }

        this.loadingProperties = false;
        this.loadBreachStateForAction();
      },
      error: () => {
        this.loadingProperties = false;
        this.toastr.error('Failed to load properties.', 'Error');
      },
    });
  }

  onSelectedProperty(property: Property): void {
    this.setSelectedProperty(property);
    this.action = 'list';
    this.loadBreachStateForAction();
  }

  onChangeProperty(): void {
    this.action = 'index';
  }

  private setSelectedProperty(property: Property): void {
    this.property = property;
    this.propertyService.setProperty(property);
    this.addBreachForm.patchValue({
      propertyId: this.property.id,
      propertyName: this.property.name,
    });
  }

  private loadBreachStateForAction(): void {
    if (this.action === 'index') {
      return;
    }

    if (!this.property?.id) {
      return;
    }

    if (this.action === 'list') {
      this.loadBreachesByProperty();
      return;
    }

    if (this.action === 'create') {
      this.addBreachForm.patchValue({
        title: '',
        description: '',
        firstname: '',
        lastname: '',
        middlename: '',
        datereported: '',
        createdBy: '',
        unit_number: '',
        propertyId: this.property?.id || '',
        bylawid: '',
        status: 'open',
        propertyName: this.property?.name || '',
      });
      return;
    }

    const breachstr = localStorage.getItem('breach');
    this.selectedBreach = breachstr ? JSON.parse(breachstr) : null;

    if (!this.selectedBreach?.id) {
      this.toastr.warning('Select a breach from the list first.', 'Info');
      this.router.navigate(['/manage-breaches/list']);
      return;
    }

    this.breachService.getBreach(this.selectedBreach.id).subscribe({
      next: (breach: Breach) => {
        this.selectedBreach = breach;
        localStorage.setItem('breach', JSON.stringify(breach));

        if (this.action === 'view') {
          this.viewBreachForm.patchValue({
            id: breach.id,
            title: breach.title,
            description: breach.description,
            firstname: breach.firstname,
            lastname: breach.lastname,
            middlename: breach.middlename || '',
            datereported: breach.datereported,
            createdBy: breach.createdBy,
            unit_number: breach.unit_number,
            propertyId: breach.propertyId,
            bylawid: breach.bylawid || '',
            status: breach.status,
            propertyName: this.property?.name || '',
          });
        }

        if (this.action === 'edit') {
          this.editBreachForm.patchValue({
            id: breach.id,
            title: breach.title,
            description: breach.description,
            firstname: breach.firstname,
            lastname: breach.lastname,
            middlename: breach.middlename || '',
            datereported: breach.datereported,
            createdBy: breach.createdBy,
            unit_number: breach.unit_number,
            propertyId: breach.propertyId,
            bylawid: breach.bylawid || '',
            status: breach.status,
            propertyName: this.property?.name || '',
          });
        }
      },
      error: (error: HttpErrorResponse) => {
        this.toastr.error('Failed to load selected breach.', 'Error');
        if (!environment.production) {
          console.log('Breach fetch failed: ', error);
        }
        this.router.navigate(['/manage-breaches/list']);
      },
    });
  }

  private loadBreachesByProperty(): void {
    if (!this.property?.id) {
      this.breaches = [];
      return;
    }

    this.loading = true;
    this.breachService.getBreachesByPropertyId(this.property.id).subscribe({
      next: (breaches: Breach[]) => {
        this.breaches = breaches || [];
        this.loading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.loading = false;
        this.toastr.error('Failed to load breaches.', 'Error');
        if (!environment.production) {
          console.log('Breach list fetch failed: ', error);
        }
      },
    });
  }

  private buildPayload(formdata: any) {
    return {
      title: formdata.title,
      description: formdata.description,
      firstname: formdata.firstname,
      lastname: formdata.lastname,
      middlename: formdata.middlename,
      datereported: formdata.datereported,
      createdBy: formdata.createdBy,
      unit_number: Number(formdata.unit_number),
      propertyId: Number(formdata.propertyId),
      bylawid: formdata.bylawid ? Number(formdata.bylawid) : null,
      status: formdata.status,
    };
  }

}
